import { truncate } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { Fragment, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { Link, useLocation } from 'react-router-dom';

import { cn } from '@workspace/ui/lib/utils';

import { RecommendationSource } from '~/api/Catalog';
import { company } from '~/company/Company';
import { createCategoryUrl } from '~/components/CategoryBlocks/utils';
import { ModalType } from '~/components/Modal/interface';
import { useGlobal } from '~/hooks/useGlobal';
import { useModal } from '~/hooks/useModal';
import { useStatusBarControl } from '~/hooks/useStatusBarControl';
import {
  PAGE_TOP_CLASS,
  STICKY_COLUMN_TOP_CLASS,
} from '~/pages/DubaiExpress/constants';
import NotFoundV3 from '~/pages/NotFoundV3';
import { ListOfItems } from '~/pages/Product/Citydrinks/ProductV3/components/ListOfItems';
import { ProductInfoBadges } from '~/pages/Product/Citydrinks/ProductV3/components/ProductInfoBadges';
import type { ProductViewProps } from '~/pages/Product/Product';
import { getProductRatingData } from '~/pages/Product/shared/getProductRatingData';
import { useProductNutritionList } from '~/pages/Product/shared/useProductNutritionList';
import { catalogStore } from '~/stores';
import { filterNutritionFields, hasPropertyValue } from '~/types/Product';

import { ProductDEActions } from './components/ProductDEActions';
import { ProductDEBuyPanel } from './components/ProductDEBuyPanel';
import { ProductDEDetails, type SpecRow } from './components/ProductDEDetails';
import { ProductDEGallery } from './components/ProductDEGallery';
import { ProductDEMobilePrice } from './components/ProductDEMobilePrice';
import { ProductDERecommendations } from './components/ProductDERecommendations';
import { ProductDEReviews } from './components/ProductDEReviews';
import { ProductDEReviewsSummary } from './components/ProductDEReviewsSummary';
import { ProductDEVariants } from './components/ProductDEVariants';
import { REVIEWS_SECTION_ID } from './constants';

interface Crumb {
  label: string;
  link?: string;
}

const formatPropertyValue = (value: unknown) =>
  Array.isArray(value) ? value.join(', ') : String(value);

/**
 * DubaiExpress product page built to the marketplace mockup: gallery, info
 * column and a sticky buy panel in three columns on desktop; a single column
 * on mobile where the price and the CTA live in the bottom bar.
 */
const ProductDE = ({
  currentItem,
  product,
  isLoading,
  isBundle,
  bundleImages,
  title,
  handleChangeOffer,
  handleChangeCount,
  count,
  limit,
  propertyList,
  badgeList,
  isMultiBuy,
  currentPrice,
  handleFavorite,
}: ProductViewProps) => {
  const { isMobile } = useGlobal();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { openModal } = useModal();

  const handleCreateReview = useCallback(() => {
    if (!product.data) return;
    catalogStore.feedbackProduct = product.data;
    openModal(ModalType.CreateReview);
  }, [product.data, openModal]);

  const characteristicsList = useMemo(
    () => filterNutritionFields(propertyList).filter(hasPropertyValue),
    [propertyList],
  );
  const nutritionList = useProductNutritionList(propertyList);

  const specRows = useMemo<SpecRow[]>(
    () => [
      ...badgeList.map(({ name }) => ({ key: name, value: t('yes') })),
      ...characteristicsList.map(({ name, value }) => ({
        key: name,
        value: formatPropertyValue(value),
      })),
      ...nutritionList.map(({ name, value, extraValue }) => ({
        key: name,
        value: extraValue ? `${value} · ${extraValue}` : value,
      })),
    ],
    [badgeList, characteristicsList, nutritionList, t],
  );

  const categoryLink = product.data?.categoryId
    ? createCategoryUrl({ id: product.data.categorySlug })
    : undefined;

  const crumbs = useMemo<Crumb[]>(() => {
    const result: Crumb[] = [
      { label: t('home'), link: '/' },
      { label: t('allCategories'), link: '/shop' },
    ];
    if (product.data?.categoryId && categoryLink) {
      result.push({
        label: truncate(product.data.categoryName, { length: 25 }),
        link: categoryLink,
      });
    }
    if (product.data?.name) {
      result.push({ label: product.data.name });
    }
    return result;
  }, [product.data, categoryLink, t]);

  useStatusBarControl({ initialColor: 'dark-content' });

  const pageTitle =
    product.selectedOffer?.metadataTitle ||
    product.data?.metadataTitle ||
    product.data?.name ||
    '';
  const pageDescription =
    product.selectedOffer?.metadataDescription ||
    product.data?.metadataDescription ||
    '';
  const canonical = company.config.canonical + (pathname ?? '');

  const loaderActive = product.request.isLoading || isLoading;
  const offerLoaderActive = loaderActive || product.isOfferLoading;

  const ratingData = getProductRatingData(product);
  const topReview = product.reviews.list[0];

  const scrollToReviews = () => {
    document
      .getElementById(REVIEWS_SECTION_ID)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (product.notFound) {
    return <NotFoundV3 />;
  }

  return (
    <>
      <Helmet>
        <title>
          {pageTitle && `${pageTitle} - `}
          {company.config.name}
        </title>
        <meta name="description" content={pageDescription} />
        <meta data-react-helmet="true" name="robots" content="index, follow" />
        <link data-react-helmet="true" rel="canonical" href={canonical} />
      </Helmet>
      <div
        className={cn(
          'mx-auto w-full max-w-[1440px] desktop:px-10 px-3.5 desktop:pt-0 pt-2.5 desktop:pb-16 pb-24 text-[#141415] antialiased [font-family:var(--fontFamily2)] [text-rendering:auto]',
          PAGE_TOP_CLASS,
        )}
      >
        <div className="desktop:flex hidden items-center gap-1.5 py-3 text-[#8A8A8A] text-[12px] leading-4">
          {crumbs.map((crumb, index) => (
            <Fragment key={crumb.label}>
              {index > 0 && <span>/</span>}
              {crumb.link ? (
                <Link
                  to={crumb.link}
                  className="no-underline! shrink-0 text-[#8A8A8A]! transition-colors duration-[120ms] hover:text-[#E94E1A]!"
                >
                  {crumb.label}
                </Link>
              ) : (
                <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[#141415]">
                  {crumb.label}
                </span>
              )}
            </Fragment>
          ))}
        </div>

        <div className="flex flex-wrap items-start gap-5">
          <div className="relative flex min-w-0 max-w-[510px] flex-[1_1_420px] flex-col gap-[18px]">
            <ProductDEGallery
              isBundle={isBundle}
              bundleImages={bundleImages}
              currentItem={currentItem}
              product={product}
              isLoading={offerLoaderActive}
            />
            {isMobile && !offerLoaderActive && (
              <ProductDEActions
                product={product}
                handleFavorite={handleFavorite}
                className="absolute top-2 right-2 z-[2]"
              />
            )}
          </div>
          <div className="flex min-w-0 flex-[1_1_360px] flex-col gap-5">
            <div>
              {offerLoaderActive ? (
                <Skeleton count={2} height={22} />
              ) : (
                <h1
                  className="m-0 text-pretty font-semibold text-[#141415] text-[20px] leading-[26px] tracking-[-0.01em]"
                  title={title}
                >
                  {title}
                </h1>
              )}
              <ProductInfoBadges variant="text" className="mt-2" />
              {isMobile && (
                <ProductDEMobilePrice
                  isMultiBuy={isMultiBuy}
                  currentPrice={currentPrice}
                  currentItem={currentItem}
                  count={count}
                  isLoading={offerLoaderActive}
                />
              )}
            </div>
            {!isBundle && (
              <ProductDEVariants
                product={product}
                handleChangeOffer={handleChangeOffer}
                isLoading={loaderActive}
              />
            )}
            {ratingData && (
              <ProductDEReviewsSummary
                rating={ratingData.averageRating}
                totalRatings={ratingData.totalRatings}
                topReview={topReview}
                onShowAll={scrollToReviews}
              />
            )}
          </div>
          {!isMobile && (
            <aside
              className={cn(
                'sticky flex min-w-[260px] flex-[0_1_300px] flex-col gap-3.5 self-start',
                STICKY_COLUMN_TOP_CLASS,
              )}
            >
              <ProductDEBuyPanel
                isMultiBuy={isMultiBuy}
                currentPrice={currentPrice}
                currentItem={currentItem}
                count={count}
                product={product}
                limit={limit}
                isBundle={isBundle}
                handleChangeCount={handleChangeCount}
                handleFavorite={handleFavorite}
                isLoading={offerLoaderActive}
              />
            </aside>
          )}
        </div>

        {!loaderActive && (
          <ProductDEDetails
            rows={specRows}
            description={currentItem?.description || undefined}
            extra={
              isBundle
                ? {
                    title: t('listOfItems'),
                    content: <ListOfItems product={product} />,
                  }
                : null
            }
          />
        )}

        <ProductDERecommendations
          source={RecommendationSource.CARD_SIMILAR}
          title={t('similarProducts')}
          seeAllLink={categoryLink}
          isLoading={loaderActive}
        />

        {company.useReviewsOnProductPage && (
          <ProductDEReviews
            reviews={product.reviews.list}
            totalCount={
              product.reviews.request.total || product.reviews.list.length
            }
            ratingData={ratingData}
            canLoadMore={product.reviews.request.canLoadMore}
            onLoadMore={product.reviews.request.loadMore}
            canCreateReview={product.reviews.canCreateReview}
            onCreateReview={handleCreateReview}
          />
        )}
      </div>
    </>
  );
};

export default observer(ProductDE);
